import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { topics } from '@/data/videoTopics';
import { ArrowLeft, Plus, Trash2, Video } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface DatabaseVideo {
  id: string;
  category_id: string;
  title: string;
  embed_code: string;
  description: string | null;
  created_at: string;
}

const AdminVideoUpload = () => {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [embedCode, setEmbedCode] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [videos, setVideos] = useState<DatabaseVideo[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(true);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVideos(data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load videos');
    } finally {
      setLoadingVideos(false);
    }
  };

  const extractTitleFromEmbed = (embed: string): string => {
    const titleMatch = embed.match(/title="([^"]+)"/);
    return titleMatch ? titleMatch[1] : '';
  };

  const handleEmbedCodeChange = (value: string) => {
    setEmbedCode(value);
    // Auto-extract title from embed code if title is empty
    if (!title) {
      const extractedTitle = extractTitleFromEmbed(value);
      if (extractedTitle) {
        setTitle(extractedTitle);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title || !embedCode || !categoryId) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('videos')
        .insert({
          title,
          embed_code: embedCode,
          category_id: categoryId,
          description: description || null,
          created_by: user?.id,
        });

      if (error) throw error;

      toast.success('Video uploaded successfully!');
      setTitle('');
      setEmbedCode('');
      setDescription('');
      setCategoryId('');
      fetchVideos();
    } catch (error: any) {
      console.error('Error uploading video:', error);
      toast.error(error.message || 'Failed to upload video');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (videoId: string) => {
    if (!confirm('Are you sure you want to delete this video?')) return;

    try {
      const { error } = await supabase
        .from('videos')
        .delete()
        .eq('id', videoId);

      if (error) throw error;

      toast.success('Video deleted successfully');
      fetchVideos();
    } catch (error: any) {
      console.error('Error deleting video:', error);
      toast.error(error.message || 'Failed to delete video');
    }
  };

  const getCategoryTitle = (catId: string) => {
    const category = topics.find(t => t.id === catId);
    return category?.title || catId;
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8 pt-24">
        <div className="mb-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/admin">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Link>
          </Button>
          <h1 className="text-3xl font-playfair font-bold text-foreground">Upload Videos</h1>
          <p className="text-muted-foreground">Add new videos to the library</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Upload Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Upload New Video
              </CardTitle>
              <CardDescription>
                Add a new video to the library by pasting the Vimeo embed code
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select value={categoryId} onValueChange={setCategoryId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {topics.map((topic) => (
                        <SelectItem key={topic.id} value={topic.id}>
                          {topic.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="embedCode">Vimeo Embed Code *</Label>
                  <Textarea
                    id="embedCode"
                    value={embedCode}
                    onChange={(e) => handleEmbedCodeChange(e.target.value)}
                    placeholder='Paste your Vimeo embed code here (e.g., <div style="padding:56.25%...)'
                    className="min-h-[120px] font-mono text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">Video Title *</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter video title"
                  />
                  <p className="text-xs text-muted-foreground">
                    Auto-extracted from embed code if available
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter a description for this video"
                  />
                </div>

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Uploading...' : 'Upload Video'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Recent Videos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video className="h-5 w-5" />
                Uploaded Videos
              </CardTitle>
              <CardDescription>
                Videos uploaded via the admin dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingVideos ? (
                <p className="text-muted-foreground text-center py-4">Loading videos...</p>
              ) : videos.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No videos uploaded yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {videos.map((video) => (
                        <TableRow key={video.id}>
                          <TableCell className="font-medium max-w-[200px] truncate">
                            {video.title}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {getCategoryTitle(video.category_id)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(video.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminVideoUpload;
